import { attributesToProps, domToReact } from 'html-react-parser'
import React, { type ReactNode } from 'react'

import { StaticPageStyledDiv } from '@hozana/dom/components/ExternalContent'
import { TTransformer, convertHtmlStringToReactComponents } from '@hozana/dom/functions'
import { useRouter } from '@hozana/router'
import { isInternalUrl } from '@hozana/router/functions'
import { EVENT_ORIGIN, TRACKING_SOURCE } from '@hozana/tracking/constants'

import { BootstrapGrid } from 'elements/special/BootstrapGrid'
import { StyledA, extractQueryInInputs } from 'elements/text/Link'

import { GuideCommunityCard } from 'modules/guide/components/GuideCommunityCard'
import { PARTNER } from 'modules/guide/constants'
import type { TGuide } from 'modules/guide/types'

import { GuideCommunityPopin } from '../popins/GuideCommunityPopin'
import { GuideRosarioMeditatioPopin } from '../popins/GuideRosarioMeditatioPopin'

const getVariant = (index: number) => String.fromCharCode(65 + index)

type TGuideContentProps = {
  guide: TGuide
  guideName: string
}

export const GuideContent: TFC<TGuideContentProps> = ({
  guide: { content, id, metaContent, partner, slug },
  guideName,
}) => {
  const { locale } = useRouter()

  /** Forms to be added to the guide content: we cannot add them with the submit button because it breaks the text layout */
  const forms: ReactNode[] = []

  /** Add origin and source params to community text links, and transform the link into a form + submit button */
  const communityTextLinkTransformer: TTransformer = (domNode) => {
    if (!('attribs' in domNode) || domNode.name !== 'a') return domNode

    const { href: rawHref, ...otherProps } = attributesToProps(domNode.attribs)
    let href = rawHref
    if (rawHref?.startsWith(CONF_KEYS.HOME)) {
      href = rawHref.slice(CONF_KEYS.HOME.length).replace(/:(\W)/g, '$1')
    }

    const match = href?.match(
      /(?:\/communaute|\/community|\/en\/community|\/it\/comunita|\/pl\/grupy|\/pt\/comunidade|\/es\/comunidad)\/(\d+)/,
    )
    const communityId = Number(match?.[1])

    if (href && isInternalUrl(href) && communityId) {
      const community = metaContent.find(({ community }) => community.id === communityId)?.community
      const toLandingPage = href.includes('/landing')

      if (community?.isSeoIndexed && !toLandingPage) {
        // If the community is seo indexed, we must not add the origin and source params to the href for SEO reasons
        return (
          <StyledA href={href} color="blue" align="left" {...otherProps}>
            {domToReact(domNode.children)}
          </StyledA>
        )
      }

      href += href.includes('?') ? '&' : '?'
      href += toLandingPage
        ? `origin=${EVENT_ORIGIN.GUIDE_TEXT_LINK}&source=${TRACKING_SOURCE.GUIDE_TEXT_LINK}&course=direct`
        : `origin=${EVENT_ORIGIN.GUIDE_TEXT_LINK}`

      const [urlWithoutQuery, inputs] = extractQueryInInputs(href)
      forms.push(
        <form id={href} action={urlWithoutQuery}>
          {inputs}
        </form>,
      )
      return (
        <StyledA as="button" type="submit" form={href} color="blue" align="left" {...otherProps}>
          {domToReact(domNode.children)}
        </StyledA>
      )
    }
    return domNode
  }

  /**
   * This popin array should be filled with 2 popins as follows:
   *
   * | Partner   | Nb guide cards |->| Popin 1   | Popin 2   |
   * |-----------|----------------|--|-----------|-----------|
   * | None      | 2+             |->| CP1       | CP2       |
   * | None      | 1              |->| CP1       | Rosario   |
   * | None      | 0              |->| Rosario   | Meditatio |
   * | Ros./Med. | 1+             |->| Ros./Med. | CP1       |
   * | Rosario   | 0              |->| Rosario   | Meditatio |
   * | Meditatio | 0              |->| Meditatio | Rosario   |
   */
  const popins: React.ReactNode[] = []

  const getRosMedPopinProps = (partner: PARTNER) => ({
    key: partner,
    options: { slug, guideName, id, locale, partner, idSuffix: popins.length + 1 },
  })

  // 1. Add the guide partner popin if any
  if (partner && Object.values(PARTNER).includes(partner))
    popins.push(<GuideRosarioMeditatioPopin {...getRosMedPopinProps(partner)} />)

  /**
   * Replace community-card patterns (e.g. [community-card id=1111]) with <GuideCommunityCard /> components
   * Also add community popins to the `popins` array
   *
   * ### Simple case
   *
   * There is no AB test: only one community card pattern in the dom node.
   * Only one popin is added to the `popins` array, without variant.
   *
   * ### Advanced case
   *
   * There is an AB test: multiple community card patterns in the dom node.
   * Example: [community-card id=1111][community-card id=2222][community-card id=3333]
   *
   * In this case, we add 3 popins at once to the `popins` array, in the same react node.
   * Each popin has a variant value: A, B, C, etc.
   */
  const communityCardTransformer: TTransformer = (domNode) => {
    if (domNode.type === 'text' && 'data' in domNode) {
      /** Matched community card patterns in the dom node */
      const matches = metaContent.reduce(
        (acc, { pattern, community }) =>
          domNode.data.includes(pattern) && community ? [...acc, { pattern, community }] : acc,
        [],
      )

      if (matches.length === 1) {
        // Simple case
        const { community } = matches[0]
        if (popins.length < 2)
          popins.push(<GuideCommunityPopin key={community.id} options={{ community, idSuffix: popins.length + 1 }} />)
        return <GuideCommunityCard community={community} />
      } else if (matches.length > 1) {
        // Advanced case
        if (popins.length < 2) {
          popins.push(
            // One `popins` array item, with multiple popins variants (A, B, C, etc.)
            <>
              {matches.map(({ community }, index) => (
                <GuideCommunityPopin
                  key={community.id}
                  options={{ community, idSuffix: popins.length + 1, variant: getVariant(index) }}
                />
              ))}
            </>,
          )
        }
        return (
          <>
            {matches.map(({ community }, index) => (
              <GuideCommunityCard key={community.id} community={community} variant={String.fromCharCode(65 + index)} />
            ))}
          </>
        )
      }
    }
    return domNode
  }

  // 2. Run the transformers on the guide content: `communityCardTransformer` will also fill the `popins` array with community popins
  const transformedContent = convertHtmlStringToReactComponents(content, [
    communityCardTransformer,
    communityTextLinkTransformer,
  ])

  /**
   * Now, in the `popins` array, we have:
   *
   * - the partner popin if any,
   * - the community popins if any.
   *
   * So the `popins` array can be in any of these cases (the brackets show the missing popins):
   *
   * | Case | Partner   | Nb guide cards |->| Popin 1    | Popin 2           | Current `popins.length` |
   * |------|-----------|----------------|--|------------|-------------------|-------------------------|
   * | 1    | None      | 2+             |->| CP1        | CP2               | 2                       |
   * | 2    | None      | 1              |->| CP1        | [Rosario]         | 1                       |
   * | 3    | None      | 0              |->| [Rosario]  | [Meditatio] if FR | 0                       |
   * | 4    | Ros./Med. | 1+             |->| Ros./Med.  | CP1               | 2                       |
   * | 5    | Rosario   | 0              |->| [Rosario]  | [Meditatio] if FR | 1                       |
   * | 6    | Meditatio | 0              |->| [Meditatio]| [Rosario]         | 1                       |
   *
   * So we now need to fill it with:
   * - first, the Rosario popin if it is not already the guide parnter (case 5), in cases 2, 3, and 6,
   * - then, the Meditatio popin, in case 3.
   */

  // 3. Add the Rosario popin if there is a popin left to fill (cases 2, 3 and 6)
  if (popins.length < 2 && partner !== PARTNER.ROSARIO)
    popins.push(<GuideRosarioMeditatioPopin {...getRosMedPopinProps(PARTNER.ROSARIO)} />)

  // 4. Add the Meditatio popin if there is a popin left to fill (case 3, 5, 6)
  if (popins.length < 2 && locale === 'fr')
    popins.push(<GuideRosarioMeditatioPopin {...getRosMedPopinProps(PARTNER.MEDITATIO)} />)

  return (
    <>
      <BootstrapGrid>
        <StaticPageStyledDiv
          fontSize={{ xs: '1rem', md: '1em', lg: '1.25rem' }}
          lineHeight="1.7em"
          mainFontFamily="noto"
        >
          {transformedContent}
          {forms}
        </StaticPageStyledDiv>
      </BootstrapGrid>
      {popins}
    </>
  )
}
